import { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ROLE, ROLE_ADMINS } from './components/constants';
import { useDispatch, useSelector } from 'react-redux';
import Nav from './components/sub-components/Nav';
import LoggedOut from './pages/LoggedOut';
import Login from './pages/Login';
import InviteRegister from './pages/InviteRegister';
import ResetPassword from './pages/ResetPassword';
import Account from './pages/Account';
import Analysis from './pages/Analysis';
import History from './pages/History';
import HomeScreen from './pages/HomeScreen';
import Settings from './pages/Settings';
import Hilfe from './pages/Hilfe';
import AddTasks from './pages/settings/AddTasks';
import { ToastContainer } from '../src/components/Toast';
import { fetchUserData } from './slice/userDataSlice';
import { fetchOrganizationColor } from './slice/themeSlice';
import { fetchTerminology } from './slice/terminologySlice';
import Users from './pages/Users';
import Chat from './pages/Chat';
import ProjectDetail from './pages/ProjectDetail';
import Agents from './pages/Agents';
import AgentSettings from './pages/AgentSettings';
import ManageOrganization from './pages/settings/ManageOrganization';
import Notifications from './pages/Notifications';
import SharedView from './pages/SharedView';
import ThemeProvider from './components/ThemeProvider';
import { LanguageProvider } from './i18n/LanguageContext';
import StaticSyncPill from './components/StaticSyncPill';
import './App.css';
import './styles/dynamic-theme.css';

const PrivateRoute = (props) => {
    const { children } = props;
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
  
    if (token) {
      localStorage.setItem('token', token);
      localStorage.setItem('email', params.get('email'));
      localStorage.setItem('name', params.get('name'));
      localStorage.setItem('id', params.get('id'));
      localStorage.setItem('language', "de");
      
      window.location.replace('/');
      return null;
    }
  
    // Allow access to logged-out/login pages without auto-redirect
    if (window.location.pathname === '/logged-out' || window.location.pathname === '/login' || window.location.pathname === '/invite' || window.location.pathname === '/reset-password') {
      return <>{children}</>;
    }

    if (!localStorage.getItem('token')) {
      window.location.href = '/login';
      return null;
    }
  
    return <>{children}</>;
  };

// Role-based route wrapper
const RoleBasedRoute = ({ children, allowedRoles = null, ...props }) => {
  const { data: userData, loading } = useSelector((state) => state.admin);
  const navigate = useNavigate();
  const location = useLocation();

  // If still loading, show nothing
  if (loading) {
    return null;
  }

  // If user has "No License" role, only allow access to hilfe page
  if (userData?.user_role === ROLE.NO_LICENSE) {
    // Always redirect to hilfe for "No License" users, except when already on hilfe
    if (location.pathname !== '/hilfe') {
      navigate('/hilfe', { replace: true });
      return null;
    }
    // Only render children if on hilfe page
    return <>{children}</>;
  }

  // For other roles, check if they have access to this route
  if (allowedRoles && !allowedRoles.includes(userData?.user_role)) {
    navigate('/hilfe', { replace: true });
    return null;
  }

  return <>{children}</>;
};

const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  // ToastContainer is imported as a global component, rendered once below
  const { data: userData, loading } = useSelector((state) => state.admin);
  const terminologyLoading = useSelector((state) => state.terminology.status === 'loading' && !state.terminology.loaded);
  const [terminologyWaitOver, setTerminologyWaitOver] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setTerminologyWaitOver(true), 3000);
    return () => clearTimeout(timer);
  }, []);
  // Initialise from persisted Nav state so the wrapper margin matches the
  // sidebar width on the very first render after a hard refresh.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('navCollapsed') === '1'; } catch { return false; }
  });

  // Listen for sidebar toggle events
  useEffect(() => {
    const handleSidebarToggle = (event) => {
      setSidebarCollapsed(event.detail.isCollapsed);
    };

    window.addEventListener('sidebarToggle', handleSidebarToggle);
    return () => {
      window.removeEventListener('sidebarToggle', handleSidebarToggle);
    };
  }, []);

  useEffect(() => {
    const email = localStorage.getItem('email');
    if (email) {
      dispatch(fetchUserData(email))
        .unwrap()
        .catch((error) => {
          console.error('Failed to fetch user data:', error);
        });

      dispatch(fetchTerminology());

      // Fetch organization color for theming
      dispatch(fetchOrganizationColor(email))
        .unwrap()
        .catch((error) => {
          console.error('Failed to fetch organization color:', error);
        });
    }
  }, [dispatch]);

  // Role-based routing effect
  useEffect(() => {
    // Only redirect if user data is loaded and user has "No License" role
    if (!loading && userData?.user_role === ROLE.NO_LICENSE) {
      // Define protected routes that "No License" users cannot access
      const protectedRoutes = ['/', '/analysis', '/history', '/settings', '/admin', '/chat', '/agents'];
      const currentPath = location.pathname;
      
      // Check if current path is a protected route
      const isProtectedRoute = protectedRoutes.some(route => 
        currentPath === route || currentPath.startsWith(route)
      );
      
      if (isProtectedRoute) {
        navigate('/hilfe', { replace: true });
      }
    }
  }, [userData, loading, location.pathname, navigate]);

  // Show loading state while checking user role
  if (loading || (terminologyLoading && !terminologyWaitOver)) {
    return (
      <LanguageProvider>
        <ThemeProvider>
          <div className="flex items-center justify-center h-screen">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-custom-yellow"></div>
          </div>
        </ThemeProvider>
      </LanguageProvider>
    );
  }

  const isFullPageRoute = location.pathname === '/logged-out' || location.pathname === '/login' || location.pathname === '/invite' || location.pathname === '/reset-password';

  return (
    <LanguageProvider>
    <ThemeProvider>
      <div className={`${isFullPageRoute ? '' : 'flex'} min-h-screen`}>
        <ToastContainer />
        <StaticSyncPill />
        {!isFullPageRoute && <Nav />}
        <div className={`flex-1 min-w-0 transition-all duration-300 ${isFullPageRoute ? '' : sidebarCollapsed ? 'ml-[72px]' : 'ml-[280px]'}`}>
          <div className="w-full">
        <Routes>
          <Route path="/" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <HomeScreen />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/analysis/:historyId" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Analysis />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/history" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <History />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/agents" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Agents />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/agents/settings/:agentId" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={ROLE_ADMINS}>
                <AgentSettings />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/project-detail/:projectId" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <ProjectDetail />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/settings" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={ROLE_ADMINS}>
                <Settings />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/account" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={ROLE_ADMINS}>
                <Account />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/settings/add" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={ROLE_ADMINS}>
                <AddTasks />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/settings/manage-organization/:orgId" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={ROLE_ADMINS}>
                <ManageOrganization />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/admin" element={
            <PrivateRoute>
              <RoleBasedRoute allowedRoles={ROLE_ADMINS}>
                <Users />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/chat" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Chat />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/chat/:id" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Chat />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/hilfe" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Hilfe />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/notifications" element={
            <PrivateRoute>
              <RoleBasedRoute>
                <Notifications />
              </RoleBasedRoute>
            </PrivateRoute>
          } />
          <Route path="/shared/:shareToken" element={<SharedView />} />
          <Route path="/logged-out" element={<LoggedOut />} />
          <Route path="/login" element={<Login />} />
          <Route path="/invite" element={<InviteRegister />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          {/* Add other routes as needed */}
        </Routes>
          </div>
        </div>
      </div>
    </ThemeProvider>
      <Toaster position="top-right" />
    </LanguageProvider>
  );
}

export default App;
